import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { SaleChannel } from '../../models/enum/sale-channel';
import { StatusChassi } from '../../models/enum/status-chassi';
import { VinValidate } from '../../models/vin-validate/vin-validate';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ChoosePlanService } from '../../services/choose-plan/choose-plan.service';
import { ClientDataService } from '../../services/client-data/client-data.service';
import { ContractSummaryService } from '../../services/contract-summary/contract-summary.service';
import { MessageExceptionService } from '../../services/message-exception/message-exception.service';
import { VehicleInformationService } from '../../services/vehicle-information/vehicle-information.service';
import { VinValidateService } from '../../services/vin-validate-service/vin-validate-service.service';

@Component({
    selector: 'app-sales-channel-external',
    templateUrl: './sales-channel-external.component.html',
    styleUrls: ['./sales-channel-external.component.css'],
    standalone: false
})
export class SalesChannelExternalComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);

	isChassi = true; // vai ficar true ate definir a regra de negocio sem chassi
	chassi = "";
	vinValidate!: VinValidate | undefined;
	inputChassiStyleCss = "";
	isValidChassi = StatusChassi.valid;
	saleChannel = SaleChannel.EXTERNAL;
	isPermission = false;
	errorMessageVin!: string;

	constructor(private vinValidateService: VinValidateService,
		private vehicleInformationService: VehicleInformationService,
		private translateService: TranslateService,
		private messageService: MessageService,
		private router: Router,
		private apsPermissionService: ApsPermissionService,
		private choosePlanService: ChoosePlanService,
		private clientDataService: ClientDataService,
		private contractSummaryService: ContractSummaryService,
		private messageExceptionService: MessageExceptionService
	) {
	}

	async ngOnInit() {
		await this.apsPermissionService.bootstrapPermissions();
		this.isPermission = this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenContractSearch);
		if (this.isPermission) {
			this.vehicleInformationService.getAllChassiValidationClean();
			this.choosePlanService.getChoosePlanBuyngClean();
			this.clientDataService.getClientDataClean();
			this.contractSummaryService.getContractSummaryClean();
		}
	}

	buttonNext() {
		this.messageService.clear();
		if (this.vinValidate && this.vinValidate.validated === StatusChassi.valid) {
			this.router.navigate(['vehicle-information', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
		} else {
			this.messageService.add({ severity: 'error', summary: this.translateService.instant('TRANSLATE.ERROR'), detail: this.translateService.instant('TRANSLATE.CHASSI.INVALID') });
		}
	}

	onKeyUp(event: KeyboardEvent): void {
		this.chassi = this.chassi.toUpperCase();
		if (this.chassi.length < 17) {
			this.inputChassiStyleCss = "";
			this.vinValidate = undefined;
			this.errorMessageVin = undefined;
		}
		if (event.key !== "Control" && this.chassi.length === 17) {
			this.buttonSearchChassi();
		}
	}

	buttonSearchChassi() {
		this.messageService.clear();
		this.vinValidateService.getAllVinValidateListClean();
		this.inputChassiStyleCss = "";

		if (this.chassi !== '') {
			this.searchChassiSaveAndVector(this.chassi);
		} else {
			this.messageService.add({ severity: 'error', summary: this.translateService.instant('TRANSLATE.ERROR'), detail: this.translateService.instant('TRANSLATE.ENTER.CHASSIS.CORRECTLY') });
		}

	}

	searchChassiSaveAndVector(chassi: string) {
		
		this.messageService.clear();
		const country: string = ConstantsFordProtect.BRA;

		this.vinValidateService.getVinValidate(country, chassi, ConstantsFordProtect.PT_BR)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: VinValidate) => {
				this.vinValidate = this.verifyChassiValidation(response);
				this.vinValidate.vin = chassi;
				this.errorMessageVin = this.vinValidate.errorMessage;

				if (this.vinValidate.validated === StatusChassi.valid
					&& this.errorMessageVin !== 'VIN.ERROR.SAVE_VECTOR') {

					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.VEHICLE.FOUND')
					});
					this.vehicleInformationService.setAllChassiValidation(this.vinValidate);
					this.setInputChassiFeedbackStyle(true);

				}
				else {

					this.setInputChassiFeedbackStyle(false);
					this.messageService.add({
						severity:'error', 
						summary: this.translateService.instant('TRANSLATE.ERROR'), 
						detail: this.translateService.instant('TRANSLATE.VEHICLE.NOT.FOUND.TYPE.AGAIN')
					});
				}
			}, (err) => {

					this.vinValidate = undefined;
					this.setInputChassiFeedbackStyle(false);
					if (err.status === 400 || err.status === 404) {

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.VEHICLE.NOT.FOUND.TYPE.AGAIN')
						});

					} else if (err.status === 401) {

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});

					} else {

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
					}
					
				});
	}

	private setInputChassiFeedbackStyle(isValid: boolean): void {
		this.inputChassiStyleCss = isValid
			? 'border: 2px solid #0c8e1c;'
			: 'border: 2px solid #ec2626;';
	}

	verifyChassiValidation(vinValidate: VinValidate): VinValidate {

		if (vinValidate.errorMessage !== null && vinValidate.errorMessage.trim().toUpperCase() === 'TRANSLATE.ERROR.SAVE_VECTOR') {
			vinValidate.validated = StatusChassi.invalid;
		} else {
			vinValidate.validated = StatusChassi.valid;
		}

		return vinValidate;
	}

	get chassisPromptLabel(): string {
		const translatedLabel = this.translateService.instant('TRANSLATE.INSERT.THE.CHASSIS');
		const currentLang = this.translateService.currentLang || this.translateService.getDefaultLang() || '';

		if (currentLang.startsWith('es')) {
			return translatedLabel.replace(/chasis\s+del/i, 'chasis<br>del');
		}

		return translatedLabel;
	}
}
